import { useState } from 'react';
import { ErrorNotice, Toggle } from '@hive/ui';
import { api, errorMessage, type Screen } from '../api/client';
import { Dialog } from './Dialog';

type Track = NonNullable<Screen['tracks']>['audio'][number];

const names = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' });
  } catch {
    return null;
  }
})();

/** "Arabic commentary (ar)", or the code when the browser does not know it. */
export function trackName(t: Pick<Track, 'language' | 'label'>): string {
  const lang = t.language ? (names?.of(t.language) ?? t.language) : 'Unknown language';
  return t.label && t.label !== lang ? `${t.label} · ${lang}` : lang;
}

const languages = (tracks: Track[]) => [...new Map(tracks.filter((t) => t.language).map((t) => [t.language!, t])).values()];

/** Audio language and subtitles for one screen, from the tracks its player found. */
export function TracksDialog({ screen, onClose, onSaved }: { screen: Screen; onClose(): void; onSaved(screen: Screen): void }) {
  const tracks = screen.tracks ?? { audio: [], text: [], preferences: { subtitles: false } };
  const [audio, setAudio] = useState(tracks.preferences.audioLanguage ?? '');
  const [subtitles, setSubtitles] = useState(tracks.preferences.subtitles);
  const [subtitleLanguage, setSubtitleLanguage] = useState(tracks.preferences.subtitleLanguage ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioChoices = languages(tracks.audio);
  const textChoices = languages(tracks.text);

  async function save() {
    setBusy(true);
    setError(null);
    const { data, error: err } = await api.PUT('/v1/screens/{screenId}/tracks', {
      params: { path: { screenId: screen.id } },
      body: { subtitles, ...(audio ? { audioLanguage: audio } : {}), ...(subtitles && subtitleLanguage ? { subtitleLanguage } : {}) },
    });
    setBusy(false);
    if (!data) return setError(errorMessage(err));
    onSaved(data);
    onClose();
  }

  return (
    <Dialog
      titleId="tracks-title"
      title={`${screen.name}: audio and subtitles`}
      subtitle="Applied on the screen straight away, and kept when the content changes."
      onClose={onClose}
      width={520}
      footer={
        <>
          <button type="button" className="hv-btn hv-btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="hv-btn hv-btn--primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="k-form">
        {error && <ErrorNotice title={error} />}
        <div className="hv-field">
          <label className="hv-field__label" htmlFor="tr-audio">
            Commentary and audio
          </label>
          <select id="tr-audio" className="hv-input" value={audio} onChange={(e) => setAudio(e.target.value)}>
            <option value="">The stream&apos;s default</option>
            {audioChoices.map((t) => (
              <option key={t.language} value={t.language}>
                {trackName(t)}
              </option>
            ))}
            {audio && !audioChoices.some((t) => t.language === audio) && <option value={audio}>{trackName({ language: audio })} (not in this stream)</option>}
          </select>
          {audioChoices.length <= 1 && <span className="k-hint">This stream has {audioChoices.length ? 'one audio language' : 'no labelled audio languages'}.</span>}
        </div>
        <Toggle label="Show subtitles" checked={subtitles} onChange={setSubtitles} />
        {subtitles && (
          <div className="hv-field">
            <label className="hv-field__label" htmlFor="tr-subs">
              Subtitle language
            </label>
            <select id="tr-subs" className="hv-input" value={subtitleLanguage} onChange={(e) => setSubtitleLanguage(e.target.value)}>
              <option value="">The stream&apos;s default</option>
              {textChoices.map((t) => (
                <option key={t.language} value={t.language}>
                  {trackName(t)}
                </option>
              ))}
            </select>
            {textChoices.length === 0 && <span className="k-hint">This stream has no subtitles right now; they will show when it does.</span>}
          </div>
        )}
      </div>
    </Dialog>
  );
}
